import { useState, useRef } from 'react';
import {
  makeStyles, tokens, Button, Spinner, Text,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { CloudAdd24Regular, CheckmarkCircle24Regular, DismissCircle24Regular } from '@fluentui/react-icons';
import { previewBatchImport, commitBatchImport, type BatchImportResponse, type RowError } from '../api/batch-import.api';
import { useT } from '../i18n';
import {
  downloadRanchosTemplate,
  downloadLotesTemplate,
  downloadUsuariosTemplate,
  downloadProductosTemplate,
  downloadCompleteTemplate,
} from '../utils/template-generator';

interface PreviewData {
  file: File;
  response: BatchImportResponse;
}

const useStyles = makeStyles({
  root: {
    padding: '24px',
    color: tokens.colorNeutralForeground1,
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '16px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  uploadCard: {
    padding: '32px',
    textAlign: 'center',
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  uploadIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  description: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginTop: '16px',
  },
  previewSection: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '20px',
    marginTop: '16px',
  },
  previewTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  previewTableHead: {
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: '600',
    padding: '10px',
    textAlign: 'left',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  previewTableCell: {
    padding: '10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  errorBanner: {
    backgroundColor: tokens.colorStatusDangerBackground1,
    color: tokens.colorStatusDangerForeground1,
    padding: '12px 16px',
    borderRadius: tokens.borderRadiusMedium,
    marginTop: '12px',
  },
  successBanner: {
    backgroundColor: tokens.colorStatusSuccessBackground1,
    color: tokens.colorStatusSuccessForeground1,
    padding: '12px 16px',
    borderRadius: tokens.borderRadiusMedium,
    marginTop: '12px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
    justifyContent: 'flex-end',
  },
  templateLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
  },
  templateLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '12px',
    marginTop: '12px',
  },
  summaryCard: {
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '12px',
    borderRadius: tokens.borderRadiusMedium,
    textAlign: 'center',
  },
});

export function BulkImportPage() {
  const s = useStyles();
  const t = useT();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setPreview(null);
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const response = await previewBatchImport(selectedFile);
      setPreview({ file: selectedFile, response });

      if (response.errors.length > 0) {
        setError(
          `${t.bulkImport.validationErrorsPrefix}${response.errors.length}${t.bulkImport.validationErrorsSuffix}`
        );
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
        t.bulkImport.readError
      );
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!preview?.file || preview.response.errors.length > 0) return;

    setCommitting(true);
    try {
      await commitBatchImport(preview.file);
      setSuccess(
        `${t.bulkImport.importSuccessPrefix}${
          (preview.response.summary.ranchos?.toCreate ?? 0) +
          (preview.response.summary.lotes?.toCreate ?? 0) +
          (preview.response.summary.usuarios?.toCreate ?? 0) +
          (preview.response.summary.catalogo?.toCreate ?? 0)
        }${t.bulkImport.importSuccessSuffix}`
      );
      setFile(null);
      setPreview(null);
      setImportDialogOpen(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || t.bulkImport.importError);
    } finally {
      setCommitting(false);
    }
  };

  const renderPreviewTable = (title: string, rows: any[], limit = 5) => {
    if (rows.length === 0) return null;

    const displayRows = rows.slice(0, limit);
    const keys = Object.keys(displayRows[0] || {}).filter(k => k !== '_row');

    return (
      <div key={title} style={{ marginBottom: '24px' }}>
        <h4 style={{ marginBottom: '12px' }}>
          {title} ({rows.length} {rows.length !== 1 ? t.bulkImport.filasLabel : t.bulkImport.filaLabel})
        </h4>
        <div style={{ overflowX: 'auto' }}>
          <table className={s.previewTable}>
            <thead>
              <tr>
                {keys.map(key => (
                  <th key={key} className={s.previewTableHead}>
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, idx) => (
                <tr key={idx}>
                  {keys.map(key => (
                    <td key={key} className={s.previewTableCell}>
                      {String(row[key] ?? '').substring(0, 50)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > limit && (
          <p style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '8px' }}>
            {t.bulkImport.previewShowingPrefix}{rows.length}{t.bulkImport.previewShowingSuffix}
          </p>
        )}
      </div>
    );
  };

  const renderErrorDetails = (errors: RowError[]) => {
    const grouped = errors.slice(0, 5).reduce(
      (acc, e) => {
        acc[e.sheet] = acc[e.sheet] || [];
        acc[e.sheet].push(`${t.bulkImport.rowLabelPrefix}${e.row}${e.field ? ` (${e.field})` : ''}: ${e.message}`);
        return acc;
      },
      {} as Record<string, string[]>
    );

    return (
      <div style={{ fontSize: '12px', marginTop: '12px' }}>
        {Object.entries(grouped).map(([sheet, messages]) => (
          <div key={sheet} style={{ marginBottom: '8px' }}>
            <strong>{sheet}:</strong>
            <ul style={{ marginTop: '4px', marginBottom: '0', paddingLeft: '20px' }}>
              {messages.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </div>
        ))}
        {errors.length > 5 && (
          <p style={{ color: tokens.colorNeutralForeground3 }}>
            {t.bulkImport.errorsMorePrefix}{errors.length - 5}{t.bulkImport.errorsMoreSuffix}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className={s.root}>
      <h1 className={s.title}>{t.bulkImport.pageTitle}</h1>

      <div className={s.section}>
        <h2 className={s.sectionTitle}>{t.bulkImport.sectionTitle}</h2>
        <p>{t.bulkImport.sectionDesc}</p>

        <div className={s.uploadCard}>
          <div className={s.uploadIcon}>
            <CloudAdd24Regular />
          </div>
          <p>{t.bulkImport.dropText}</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            id="file-input"
            onChange={handleFileSelect}
            disabled={loading}
          />
          <label htmlFor="file-input" style={{ display: 'inline-block' }}>
            <Button
              appearance="primary"
              disabled={loading}
              style={{ marginTop: '12px', cursor: loading ? 'default' : 'pointer' }}
            >
              {loading ? t.bulkImport.processing : t.bulkImport.selectFile}
            </Button>
          </label>
          <div className={s.description}>
            {t.bulkImport.formatsSupported}
          </div>

          {file && !loading && (
            <div style={{ marginTop: '12px' }}>
              <Text size={300} weight="semibold">
                📎 {file.name}
              </Text>
            </div>
          )}

          {loading && (
            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Spinner size="small" />
              <Text size={300}>{t.bulkImport.analyzing}</Text>
            </div>
          )}

          {error && (
            <div className={s.errorBanner}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <DismissCircle24Regular />
                <div>
                  <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>{t.bulkImport.validationErrorTitle}</p>
                  <p style={{ margin: '0' }}>{error}</p>
                  {preview?.response?.errors && renderErrorDetails(preview.response.errors)}
                </div>
              </div>
            </div>
          )}

          {preview && preview.response.errors.length === 0 && (
            <div className={s.successBanner}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckmarkCircle24Regular />
                <Text>{t.bulkImport.validationSuccess}</Text>
              </div>
            </div>
          )}
        </div>

        {preview && (
          <div className={s.previewSection}>
            <h3>{t.bulkImport.summaryTitle}</h3>
            <div className={s.summaryGrid}>
              <div className={s.summaryCard}>
                <div style={{ fontSize: '20px', fontWeight: '700' }}>
                  {preview.response.summary.ranchos?.toCreate ?? 0}
                </div>
                <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{t.bulkImport.summaryRanchos}</div>
              </div>
              <div className={s.summaryCard}>
                <div style={{ fontSize: '20px', fontWeight: '700' }}>
                  {preview.response.summary.lotes?.toCreate ?? 0}
                </div>
                <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{t.bulkImport.summaryLotes}</div>
              </div>
              <div className={s.summaryCard}>
                <div style={{ fontSize: '20px', fontWeight: '700' }}>
                  {preview.response.summary.usuarios?.toCreate ?? 0}
                </div>
                <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{t.bulkImport.summaryUsuarios}</div>
              </div>
              <div className={s.summaryCard}>
                <div style={{ fontSize: '20px', fontWeight: '700' }}>
                  {preview.response.summary.catalogo?.toCreate ?? 0}
                </div>
                <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{t.bulkImport.summaryProductos}</div>
              </div>
            </div>

            <h4 style={{ marginTop: '24px', marginBottom: '12px' }}>{t.bulkImport.previewTitle}</h4>
            {preview.response.preview?.ranchos && renderPreviewTable(t.bulkImport.summaryRanchos, preview.response.preview.ranchos)}
            {preview.response.preview?.lotes && renderPreviewTable(t.bulkImport.summaryLotes, preview.response.preview.lotes)}
            {preview.response.preview?.usuarios && renderPreviewTable(t.bulkImport.summaryUsuarios, preview.response.preview.usuarios)}
            {preview.response.preview?.catalogo && renderPreviewTable(t.bulkImport.summaryProductos, preview.response.preview.catalogo)}

            <div className={s.buttonGroup}>
              <Button
                onClick={() => {
                  setFile(null);
                  setPreview(null);
                  setError(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                {t.bulkImport.cancel}
              </Button>
              <Button
                appearance="primary"
                disabled={preview.response.errors.length > 0 || committing}
                onClick={() => setImportDialogOpen(true)}
              >
                {committing ? t.bulkImport.importing : t.bulkImport.confirmImport}
              </Button>
            </div>
          </div>
        )}

        {success && (
          <div className={s.successBanner}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckmarkCircle24Regular />
              <Text>{success}</Text>
            </div>
          </div>
        )}
      </div>

      <div className={s.section}>
        <h2 className={s.sectionTitle}>{t.bulkImport.templatesTitle}</h2>
        <p>{t.bulkImport.templatesDesc}</p>
        <div className={s.templateLinks}>
          <button
            className={s.templateLink}
            onClick={downloadRanchosTemplate}
            style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left' }}
          >
            📄 {t.bulkImport.templateRanchos}
          </button>
          <button
            className={s.templateLink}
            onClick={downloadLotesTemplate}
            style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left' }}
          >
            📄 {t.bulkImport.templateLotes}
          </button>
          <button
            className={s.templateLink}
            onClick={downloadUsuariosTemplate}
            style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left' }}
          >
            📄 {t.bulkImport.templateUsuarios}
          </button>
          <button
            className={s.templateLink}
            onClick={downloadProductosTemplate}
            style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left' }}
          >
            📄 {t.bulkImport.templateProductos}
          </button>
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
            <Button appearance="secondary" onClick={downloadCompleteTemplate} style={{ width: '100%' }}>
              ⬇️ {t.bulkImport.downloadComplete}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={importDialogOpen} onOpenChange={(_, data) => setImportDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.bulkImport.confirmDialogTitle}</DialogTitle>
            <DialogContent>
              <p>
                {t.bulkImport.confirmDialogBody}
              </p>
              <p style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                {t.bulkImport.confirmDialogNotePrefix}
                {(preview?.response.summary.ranchos?.toCreate ?? 0) +
                  (preview?.response.summary.lotes?.toCreate ?? 0) +
                  (preview?.response.summary.usuarios?.toCreate ?? 0) +
                  (preview?.response.summary.catalogo?.toCreate ?? 0)}
                {t.bulkImport.confirmDialogNoteSuffix}
              </p>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setImportDialogOpen(false)}>{t.bulkImport.cancel}</Button>
              <Button
                appearance="primary"
                onClick={handleImport}
                disabled={committing}
              >
                {committing ? t.bulkImport.importing : t.bulkImport.confirm}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
